/**
 * Tables Tab Component
 * Manages tables listing, search, create, and edit
 */

import { apiSuccess } from '@/renderer/util/notify';
import emptyBoxAnimation from '@assets/lottie/empty-box.json';
import { selectEffectiveOutletId } from '@store/outlets';
import { Plus, Search } from 'lucide-react';
import React, { useCallback, useMemo, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../../hooks/useRedux';
import { ONBOARDING_TOUR_TARGETS } from '../../../services/onboardingTourService';
import {
  selectAllSections,
  selectAllTables,
  selectSectionsLoading,
} from '../../../store/sections/sectionSelectors';
import {
  createTableThunk,
  updateTableThunk,
} from '../../../store/sections/sectionThunks';
import { OutletTable } from '../../../types/section';
import { EmptyState, LoadingState } from '../../common';
import { TableFormModal } from './TableFormModal';
import { TableRow } from './TableRow';

interface TablesTabProps {
  canManage?: boolean;
}

export const TablesTab: React.FC<TablesTabProps> = ({ canManage = true }) => {
  const dispatch = useAppDispatch();
  const tables = useAppSelector(selectAllTables);
  const sections = useAppSelector(selectAllSections);
  const isLoading = useAppSelector(selectSectionsLoading);
  const outletId = useAppSelector(selectEffectiveOutletId);

  const [searchQuery, setSearchQuery] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingTable, setEditingTable] = useState<OutletTable | null>(null);

  const filteredTables = useMemo(() => {
    if (!searchQuery.trim()) return tables;
    const query = searchQuery.toLowerCase();
    return tables.filter(
      (t) =>
        t.name.toLowerCase().includes(query) ||
        (t.section_name && t.section_name.toLowerCase().includes(query)),
    );
  }, [tables, searchQuery]);

  const handleCreate = useCallback(
    async (name: string, sectionId: number, isHidden: boolean) => {
      if (!canManage) return;
      await dispatch(
        createTableThunk({
          name,
          section_id: sectionId,
          is_hidden: isHidden,
        }),
      ).unwrap();
      apiSuccess('Table was created');
      // setIsModalOpen(false);
    },
    [canManage, dispatch],
  );

  const handleUpdate = useCallback(
    async (name: string, _sectionId: number, isHidden: boolean) => {
      if (!editingTable || !canManage) return;
      await dispatch(
        updateTableThunk({
          id: editingTable.id,
          name,
          is_hidden: isHidden,
        }),
      );
      setEditingTable(null);
    },
    [canManage, dispatch, editingTable],
  );

  const handleEdit = useCallback(
    (table: OutletTable) => {
      if (!canManage) return;
      setEditingTable(table);
    },
    [canManage],
  );

  if (isLoading && tables.length === 0) {
    return <LoadingState message="Loading tables..." />;
  }

  return (
    <div>
      {/* Toolbar */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => setIsModalOpen(true)}
            data-onboarding-tour={ONBOARDING_TOUR_TARGETS.tablesAddButton}
            className="p-2 text-pos-primary hover:bg-pos-light/30 rounded-lg transition-colors disabled:cursor-not-allowed disabled:opacity-50"
            title="Add Table"
            disabled={!outletId || sections.length === 0 || !canManage}
          >
            <Plus size={20} />
          </button>
          <div className="relative">
            <Search
              size={16}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
            />
            <input
              type="text"
              placeholder="Search"
              aria-label="Search tables"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 pr-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-pos-primary focus:border-pos-primary transition-colors w-40"
            />
          </div>
        </div>
      </div>

      {/* Table */}
      {filteredTables.length === 0 ? (
        <EmptyState
          title="No tables found"
          icon="lottie"
          lottieData={emptyBoxAnimation}
          description={
            !outletId
              ? 'Select a specific outlet to add tables. You are currently viewing tables across all outlets.'
              : sections.length === 0
                ? 'Create a section first, then add tables to it.'
                : searchQuery
                  ? 'Try adjusting your search query.'
                  : 'Create your first table and assign it to a section.'
          }
          primaryAction={
            !searchQuery &&
            Boolean(outletId) &&
            sections.length > 0 &&
            canManage
              ? {
                  dataTourId: ONBOARDING_TOUR_TARGETS.tablesAddButton,
                  label: 'Add Table',
                  onClick: () => setIsModalOpen(true),
                  icon: <Plus size={18} />,
                }
              : undefined
          }
        />
      ) : (
        <div
          className="bg-white rounded-xl border border-gray-100 overflow-hidden max-h-[calc(100vh-400px)] overflow-y-auto"
          data-onboarding-tour={ONBOARDING_TOUR_TARGETS.tablesTable}
        >
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50/50">
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Name
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Outlet
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Section
                </th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Active
                </th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredTables.map((table) => (
                <TableRow
                  key={table.id}
                  canManage={canManage}
                  table={table}
                  showOutletColumn
                  onEdit={handleEdit}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create Modal */}
      <TableFormModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSubmit={handleCreate}
        sections={sections}
        isLoading={isLoading}
      />

      {/* Edit Modal */}
      <TableFormModal
        isOpen={!!editingTable}
        onClose={() => setEditingTable(null)}
        onSubmit={handleUpdate}
        sections={sections}
        table={editingTable}
        isLoading={isLoading}
      />
    </div>
  );
};
